import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { VisualizerState, PaintSettings, StanceSettings, WheelSettings, VehicleSilhouetteId } from '../types/vehicle';

import {
  CameraPresetId,
  LightingPresetId,
  CameraPreset,
  CAMERA_PRESETS,
  ModelConfig,
  MODEL_CONFIGS,
} from '../types/threeStudio';
import { classifyMeshRole, MeshRole } from './meshRoleClassifier';
import {
  derivePaintSpec,
  deriveWheelSpec,
  deriveTireSpec,
  deriveBrakeSpec,
  deriveGlassSpec,
  MaterialSpec,
} from './materialSpecs';
import { computeModelPlacement, computeLocalBounds } from './modelPlacement';
import { solveCameraPose } from './cameraPoseSolver';
import { buildStudioEnvironmentTexture } from './studioEnvironment';

export type {
  CameraPresetId,
  LightingPresetId,
  CameraPreset,
  ModelConfig,
};
export {
  CAMERA_PRESETS,
  MODEL_CONFIGS,
};

type ResettableRole = Exclude<MeshRole, 'other'>;

const TURNABLE_TOP_Y = 0.03; // turntable disc surface height

export class ThreeCarRenderer {
  private container: HTMLElement | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private controls: OrbitControls | null = null;
  /** Set once the user drags the camera, so auto-framing stops fighting them. */
  private userHasOrbited: boolean = false;
  private animationFrameId: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  // Lighting
  private ambientLight: THREE.AmbientLight | null = null;
  private hemisphereLight: THREE.HemisphereLight | null = null;
  private keyLight: THREE.DirectionalLight | null = null;
  private fillLight: THREE.DirectionalLight | null = null;
  private rimLight: THREE.DirectionalLight | null = null;
  private overheadLight: THREE.PointLight | null = null;

  // Turntable and Environment
  private turntableGroup: THREE.Group | null = null;
  private gridHelper: THREE.GridHelper | null = null;
  private autoRotateTurntable: boolean = false;
  private currentLightingMode: LightingPresetId = 'industrial_shop';

  // Car model structure
  private carRootGroup: THREE.Group | null = null;
  private chassisGroup: THREE.Group | null = null;
  /**
   * One material per render role, shared by every mesh of that role so a single
   * paint change is one write rather than one per mesh.
   */
  private roleMaterials: Record<ResettableRole, THREE.MeshPhysicalMaterial> | null = null;
  private wheelNodes: {
    frontLeft?: THREE.Object3D;
    frontRight?: THREE.Object3D;
    rearLeft?: THREE.Object3D;
    rearRight?: THREE.Object3D;
    all: THREE.Object3D[];
  } = { all: [] };
  /** Bounds of the loaded model in world space, for camera framing. */
  private modelBounds: THREE.Box3 | null = null;

  // Loaders (reused across model loads)
  private gltfLoader: GLTFLoader | null = null;
  private dracoLoader: DRACOLoader | null = null;
  private environmentTexture: THREE.Texture | null = null;

  // Current state tracking
  private currentState: VisualizerState | null = null;
  private currentLoadedSilhouette: string | null = null;
  private isLoadingModel: boolean = false;

  // Camera transition animation
  private isTransitioningCamera: boolean = false;
  private cameraTransitionStart: number = 0;
  private cameraTransitionDuration: number = 400; // ms
  private currentCameraPreset: CameraPresetId = 'beauty_3_4';
  private cameraStartPos = new THREE.Vector3();
  private cameraEndPos = new THREE.Vector3();
  private cameraStartTarget = new THREE.Vector3();
  private cameraEndTarget = new THREE.Vector3();

  // Callbacks
  private onLoadedCallback?: () => void;
  private onErrorCallback?: (err: Error) => void;

  constructor() {}

  /**
   * Mount renderer into DOM container and bootstrap scene
   */
  public mount(
    container: HTMLElement,
    initialState: VisualizerState,
    options?: {
      onLoaded?: () => void;
      onError?: (err: Error) => void;
    }
  ): void {
    this.container = container;
    this.currentState = initialState;
    this.onLoadedCallback = options?.onLoaded;
    this.onErrorCallback = options?.onError;

    const width = container.clientWidth || 800;
    const height = container.clientHeight || 450;

    // 1. Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0e14); // Slate deepest shop background

    // 2. Camera (38 deg FOV for realistic automotive proportions, minimal wide-angle lens distortion)
    this.camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 50.0);
    // Replaced with a bounds-fitted pose as soon as a model loads; this is only
    // the fallback for the moment before then.
    this.camera.position.set(4.2, 1.4, 3.4);

    // 3. WebGL Renderer
    try {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
        preserveDrawingBuffer: true, // for instant high-res PNG snapshots
      });
    } catch (err) {
      console.warn('WebGL initialization failed:', err);
      this.onErrorCallback?.(err as Error);
      return;
    }

    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // 3b. Image-based lighting. Without something to reflect, metalness renders
    // as flat dark plastic no matter how many analytic lights are added.
    this.environmentTexture = buildStudioEnvironmentTexture(this.renderer);
    if (this.environmentTexture) {
      this.scene.environment = this.environmentTexture;
      this.scene.environmentIntensity = 1.0;
    }

    container.innerHTML = '';
    container.appendChild(this.renderer.domElement);
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';

    // 4. OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.target.set(0, 0.45, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2 - 0.02; // prevent camera clipping under ground
    this.controls.minDistance = 2.0;
    this.controls.maxDistance = 24.0;
    this.controls.enablePan = true;
    this.controls.addEventListener('start', () => {
      this.userHasOrbited = true;
    });

    // 5. Environment & Turntable
    this.setupEnvironment();

    // 6. Lighting Rigs
    this.setupLighting();

    // 7. Load Vehicle Model
    this.loadVehicleModel(initialState.silhouetteId);

    // 8. Start Render Loop
    this.startAnimationLoop();

    // 9. Resize Observer
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        const newHeight = entry.contentRect.height;
        if (newWidth > 0 && newHeight > 0 && this.renderer && this.camera) {
          this.camera.aspect = newWidth / newHeight;
          this.camera.updateProjectionMatrix();
          this.renderer.setSize(newWidth, newHeight);
          // A fixed distance does not re-frame when the viewport changes shape,
          // so the crop appears on resize. Re-solve against the same preset.
          this.frameCamera(this.currentCameraPreset);
        }
      }
    });
    this.resizeObserver.observe(container);
  }

  /**
   * Set up workshop turntable platform and CAD alignment grid
   */
  private setupEnvironment(): void {
    if (!this.scene) return;

    this.turntableGroup = new THREE.Group();

    // Circular Turntable Disc (Machined Slate Shop Platform)
    const turntableRadius = 3.6;
    const turntableHeight = 0.03;
    const turntableGeo = new THREE.CylinderGeometry(
      turntableRadius,
      turntableRadius + 0.04,
      turntableHeight,
      64
    );
    const turntableMat = new THREE.MeshStandardMaterial({
      color: 0x141a23, // Deep machined slate
      roughness: 0.55,
      metalness: 0.65,
    });
    const turntableMesh = new THREE.Mesh(turntableGeo, turntableMat);
    turntableMesh.position.y = turntableHeight / 2;
    turntableMesh.receiveShadow = true;
    this.turntableGroup.add(turntableMesh);

    // Turntable Outer Machined Ring (Brushed Steel Bevel)
    const ringGeo = new THREE.RingGeometry(turntableRadius - 0.08, turntableRadius + 0.03, 64);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0x384656, // Industrial brushed steel
      roughness: 0.35,
      metalness: 0.85,
      side: THREE.DoubleSide,
    });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    ringMesh.rotation.x = -Math.PI / 2;
    ringMesh.position.y = turntableHeight + 0.001;
    this.turntableGroup.add(ringMesh);

    // Subtle Turntable Degree Alignment Ticks
    const innerRingGeo = new THREE.RingGeometry(1.6, 1.62, 64);
    const innerRingMat = new THREE.MeshBasicMaterial({
      color: 0x222c38,
      side: THREE.DoubleSide,
    });
    const innerRingMesh = new THREE.Mesh(innerRingGeo, innerRingMat);
    innerRingMesh.rotation.x = -Math.PI / 2;
    innerRingMesh.position.y = turntableHeight + 0.001;
    this.turntableGroup.add(innerRingMesh);

    // Outer Workshop Floor Plane
    const floorGeo = new THREE.PlaneGeometry(60, 60);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x080b0f,
      roughness: 0.88,
      metalness: 0.15,
    });
    const floorMesh = new THREE.Mesh(floorGeo, floorMat);
    floorMesh.rotation.x = -Math.PI / 2;
    floorMesh.position.y = 0;
    floorMesh.receiveShadow = true;
    this.scene.add(floorMesh);

    // Workshop CAD Alignment Grid
    this.gridHelper = new THREE.GridHelper(24, 48, 0x384656, 0x161e28);
    this.gridHelper.position.y = 0.002;
    this.scene.add(this.gridHelper);

    this.scene.add(this.turntableGroup);
  }

  /**
   * Set up studio lighting rigs with multi-light environment
   */
  private setupLighting(): void {
    if (!this.scene) return;

    // Base tone. Kept low on purpose: a large flat ambient term is exactly what
    // washes out the specular structure the studio probe provides.
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.18);
    this.scene.add(this.ambientLight);

    // Sky/ground separation only.
    this.hemisphereLight = new THREE.HemisphereLight(0x2a3649, 0x0a0e14, 0.35);
    this.scene.add(this.hemisphereLight);

    // Main Studio Key Light (Angled 45 deg, casts soft automotive shadows)
    this.keyLight = new THREE.DirectionalLight(0xfff7ed, 2.2);
    this.keyLight.position.set(5.0, 5.5, 4.5);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.width = 2048;
    this.keyLight.shadow.mapSize.height = 2048;
    this.keyLight.shadow.camera.near = 0.5;
    this.keyLight.shadow.camera.far = 20;
    // Must cover the longest silhouette plus the bolt-on parts; the Mustang is
    // 4.64 m, and a 4 m half-extent crops its shadow at the bumpers.
    this.keyLight.shadow.camera.left = -6;
    this.keyLight.shadow.camera.right = 6;
    this.keyLight.shadow.camera.top = 6;
    this.keyLight.shadow.camera.bottom = -6;
    this.keyLight.shadow.bias = -0.0005;
    // Car bodywork is smooth and curved: without a normal bias it self-shadows
    // into acne, and without a radius the shadow edge is a hard stencil.
    this.keyLight.shadow.normalBias = 0.02;
    this.keyLight.shadow.radius = 3;
    this.scene.add(this.keyLight);

    // Fill Light (Cool daylight tone from opposite quarter)
    this.fillLight = new THREE.DirectionalLight(0xcfd8dc, 1.2);
    this.fillLight.position.set(-4.5, 4.0, -3.5);
    this.scene.add(this.fillLight);

    // Rear High Rim Light (Highlights roof curvature and fastback quarter flares)
    this.rimLight = new THREE.DirectionalLight(0x90caf9, 1.4);
    this.rimLight.position.set(-3.5, 3.8, 4.0);
    this.scene.add(this.rimLight);

    // Overhead High-Bay Fluorescent Strip Light
    this.overheadLight = new THREE.PointLight(0xffffff, 1.8, 12, 1.2);
    this.overheadLight.position.set(0, 4.8, 0);
    this.scene.add(this.overheadLight);
  }

  /**
   * Set lighting preset mode
   */
  public setLightingEnvironment(mode: LightingPresetId): void {
    this.currentLightingMode = mode;
    if (!this.keyLight || !this.fillLight || !this.rimLight || !this.overheadLight || !this.ambientLight) return;

    if (mode === 'industrial_shop') {
      // Balanced cool high-bay lighting with crisp mechanical contrast
      this.keyLight.color.setHex(0xfff7ed);
      this.keyLight.intensity = 2.2;
      this.fillLight.color.setHex(0xcfd8dc);
      this.fillLight.intensity = 1.2;
      this.rimLight.color.setHex(0x90caf9);
      this.rimLight.intensity = 1.4;
      this.overheadLight.intensity = 1.8;
      this.ambientLight.intensity = 0.18;
    } else if (mode === 'showroom_spotlight') {
      // High-contrast dramatic spotlights with deep blacks and sharp reflections
      this.keyLight.color.setHex(0xffffff);
      this.keyLight.intensity = 3.2;
      this.fillLight.color.setHex(0x64748b);
      this.fillLight.intensity = 0.5;
      this.rimLight.color.setHex(0x38bdf8);
      this.rimLight.intensity = 2.0;
      this.overheadLight.intensity = 0.8;
      this.ambientLight.intensity = 0.08;
    } else if (mode === 'daylight') {
      // Warm golden natural exterior sun
      this.keyLight.color.setHex(0xffedd5);
      this.keyLight.intensity = 3.0;
      this.fillLight.color.setHex(0xbfdbfe);
      this.fillLight.intensity = 1.6;
      this.rimLight.color.setHex(0xfef08a);
      this.rimLight.intensity = 1.0;
      this.overheadLight.intensity = 1.0;
      this.ambientLight.intensity = 0.30;
    }
  }

  /**
   * Load 3D Vehicle Model based on silhouette ID
   */
  public loadVehicleModel(silhouetteId: VehicleSilhouetteId | string): void {
    if (!this.scene) return;
    if (this.currentLoadedSilhouette === silhouetteId && this.carRootGroup) {
      // Already loaded, just apply current stance and paint
      if (this.currentState) {
        this.update(this.currentState);
      }
      return;
    }

    const config = MODEL_CONFIGS[silhouetteId] || MODEL_CONFIGS.classic_muscle;
    this.isLoadingModel = true;

    // Remove existing vehicle from scene if any
    if (this.carRootGroup) {
      this.scene.remove(this.carRootGroup);
      this.disposeObject(this.carRootGroup);
      this.carRootGroup = null;
      this.chassisGroup = null;
      this.roleMaterials = null;
      this.wheelNodes = { all: [] };
      this.modelBounds = null;
    }

    if (config.meshLayoutNote) {
      console.info(`[3D Studio] ${silhouetteId}: ${config.meshLayoutNote}`);
    }
    if (config.isPlaceholderMesh) {
      console.info(`[3D Studio] ${silhouetteId}: rendering a placeholder mesh (${config.path}).`);
    }

    const gltfLoader = this.ensureLoaders();

    gltfLoader.load(
      config.path,
      (gltf) => {
        if (!this.scene) return;

        this.carRootGroup = new THREE.Group();
        this.chassisGroup = new THREE.Group();
        this.carRootGroup.add(this.chassisGroup);

        const loadedScene = gltf.scene;

        // Measure in the model's OWN local space, then place, then rotate.
        // Computing bounds after the wrapper rotation is what makes a car drift
        // off centre as soon as the user orbits.
        const localBounds = computeLocalBounds(loadedScene);
        const boundsSize = localBounds.getSize(new THREE.Vector3());
        const measuredLength = Math.max(boundsSize.x, boundsSize.z);
        const modelScale = config.realLengthMeters && measuredLength > 0
          ? config.realLengthMeters / measuredLength
          : (config.scale ?? 1);

        const placement = computeModelPlacement({
          localBounds,
          scale: modelScale,
          rotationY: config.rotationY,
          offsetY: config.offsetY,
          groundY: TURNABLE_TOP_Y,
        });

        loadedScene.position.set(
          placement.position[0],
          placement.position[1],
          placement.position[2]
        );
        loadedScene.rotation.y = placement.rotationY;
        loadedScene.scale.setScalar(placement.scale);

        this.chassisGroup.add(loadedScene);

        // Inspect meshes and classify roles
        this.classifyModelMeshes(loadedScene, config);

        this.scene.add(this.carRootGroup);
        this.currentLoadedSilhouette = silhouetteId;
        this.isLoadingModel = false;

        // Measure world bounds AFTER placement, for camera framing.
        this.carRootGroup.updateMatrixWorld(true);
        this.modelBounds = new THREE.Box3().setFromObject(this.carRootGroup);

        // Apply initial visualizer state (paint, stance, wheels)
        if (this.currentState) {
          this.applyPaint(this.currentState.paint);
          this.applyStance(this.currentState.stance, config);
          this.applyWheels(this.currentState.wheel);
        }

        // Frame the car with the active preset, now that its size is known.
        this.applyCameraPresetImmediately(this.currentCameraPreset);

        this.onLoadedCallback?.();
      },
      undefined,
      (err) => {
        console.warn(`Failed to load 3D model ${config.path}:`, err);
        this.isLoadingModel = false;
        this.onErrorCallback?.(err as Error);
      }
    );
  }

  /**
   * One GLTF/DRACO loader pair for the renderer's lifetime. Constructing them
   * per load re-fetches and re-initialises the Draco wasm worker every time the
   * user switches silhouettes.
   */
  private ensureLoaders(): GLTFLoader {
    if (!this.dracoLoader) {
      this.dracoLoader = new DRACOLoader();
      this.dracoLoader.setDecoderPath('/javascripts/draco/');
      this.dracoLoader.preload();
    }
    if (!this.gltfLoader) {
      this.gltfLoader = new GLTFLoader();
      this.gltfLoader.setDRACOLoader(this.dracoLoader);
    }
    return this.gltfLoader;
  }

  /**
   * Traverse model hierarchy and classify body carpaint, wheels, and suspension nodes
   */
  private classifyModelMeshes(root: THREE.Object3D, config: ModelConfig): void {
    this.wheelNodes = { all: [] };

    const currentPaint = this.currentState?.paint;
    const roleMaterials: Record<ResettableRole, THREE.MeshPhysicalMaterial> = {
      body: this.createRoleMaterial(derivePaintSpec(
        currentPaint ?? {
          finishType: 'acrylic_lacquer',
          colorHex: '#ea580c',
          clearcoatGloss: 0.9,
          metallicFleckDensity: 0,
        }
      )),
      wheel_rim: this.createRoleMaterial(deriveWheelSpec('machined_gray')),
      tire: this.createRoleMaterial(deriveTireSpec(this.currentState?.wheel.tireTreatment ?? 'blackwall')),
      brake: this.createRoleMaterial(deriveBrakeSpec()),
      glass: this.createRoleMaterial(deriveGlassSpec()),
    };
    this.roleMaterials = roleMaterials;

    const wheelCandidates: THREE.Object3D[] = [];

    root.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;

      mesh.castShadow = true;
      mesh.receiveShadow = true;

      const materialNames = Array.isArray(mesh.material)
        ? mesh.material.map((m) => m.name)
        : [mesh.material?.name ?? ''];

      const role = classifyMeshRole(
        {
          nodeName: mesh.name || '',
          parentName: mesh.parent?.name || '',
          materialNames,
        },
        config.meshRoleRules
      );

      if (role === 'other') return; // keep the authored material untouched

      mesh.material = roleMaterials[role];

      if (role === 'wheel_rim' || role === 'tire') {
        wheelCandidates.push(mesh);
      }
    });

    this.assignWheelNodes(wheelCandidates, config.usesQuadrantWheelDetection === true);
  }

  /**
   * Point the four stance slots at real wheel meshes.
   *
   * Two strategies, because the assets differ: the Shelby and Camaro name their
   * corners ('GT500.Wheel.Ft.R', 'Wheel_Back_L'), while the Mustang merges every
   * wheel into one primitive whose node name says nothing, so the corners are
   * read off world position instead.
   */
  private assignWheelNodes(candidates: THREE.Object3D[], useQuadrants: boolean): void {
    this.wheelNodes = { all: candidates };
    if (candidates.length === 0) return;

    const byName = (node: THREE.Object3D): string =>
      `${node.name} ${node.parent?.name ?? ''}`.toLowerCase();

    const named = {
      frontLeft: candidates.find((n) => /ft\.?l|front_?l|wheel_?l/.test(byName(n)) && !/bk|back|rear/.test(byName(n))),
      frontRight: candidates.find((n) => /ft\.?r|front_?r|wheel_?r/.test(byName(n)) && !/bk|back|rear/.test(byName(n))),
      rearLeft: candidates.find((n) => /bk\.?l|back_?l|rear_?l/.test(byName(n))),
      rearRight: candidates.find((n) => /bk\.?r|back_?r|rear_?r/.test(byName(n))),
    };

    if (named.frontLeft && named.frontRight && named.rearLeft && named.rearRight) {
      this.wheelNodes = { ...named, all: candidates };
      return;
    }

    if (!useQuadrants) {
      this.wheelNodes = { all: candidates };
      return;
    }

    // Quadrant pass: split on the model's own bounds. A single merged tire mesh
    // lands in every quadrant at once, so it is used for all four slots, which
    // is exactly the uniform-scale fallback applyStance already handles.
    const box = new THREE.Box3();
    for (const node of candidates) box.expandByObject(node);
    if (box.isEmpty()) {
      this.wheelNodes = { all: candidates };
      return;
    }

    const front = candidates.find((n) => {
      const p = new THREE.Vector3();
      n.getWorldPosition(p);
      return p.z > box.getCenter(new THREE.Vector3()).z;
    }) ?? candidates[0];

    this.wheelNodes = {
      frontLeft: front,
      frontRight: front,
      rearLeft: front,
      rearRight: front,
      all: candidates,
    };
  }

  private createRoleMaterial(spec: MaterialSpec): THREE.MeshPhysicalMaterial {
    return new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(spec.colorHex),
      metalness: spec.metalness,
      roughness: spec.roughness,
      clearcoat: spec.clearcoat,
      clearcoatRoughness: spec.clearcoatRoughness,
      envMapIntensity: spec.envMapIntensity,
      anisotropy: spec.anisotropy,
      reflectivity: spec.reflectivity,
    });
  }

  /** Push a spec onto one role's live material, or every material at once. */
  private applySpec(role: ResettableRole | 'all', spec: MaterialSpec): void {
    if (!this.roleMaterials) return;
    const targets = role === 'all' ? Object.values(this.roleMaterials) : [this.roleMaterials[role]];
    for (const material of targets) {
      material.color.set(spec.colorHex);
      material.metalness = spec.metalness;
      material.roughness = spec.roughness;
      material.clearcoat = spec.clearcoat;
      material.clearcoatRoughness = spec.clearcoatRoughness;
      material.envMapIntensity = spec.envMapIntensity;
      material.anisotropy = spec.anisotropy;
      material.reflectivity = spec.reflectivity;
      material.needsUpdate = true;
    }
  }

  /**
   * Update visualizer scene with new state (instant 60fps hot updates)
   */
  public update(state: VisualizerState): void {
    this.currentState = state;

    // 1. If silhouette changed, reload model
    if (state.silhouetteId !== this.currentLoadedSilhouette && !this.isLoadingModel) {
      this.loadVehicleModel(state.silhouetteId);
      return;
    }

    const config = MODEL_CONFIGS[state.silhouetteId] || MODEL_CONFIGS.classic_muscle;

    // 2. Apply Paint
    this.applyPaint(state.paint);

    // 3. Apply Stance & Suspension Rake
    this.applyStance(state.stance, config);

    // 4. Apply Wheel Finishes & Archetypes
    this.applyWheels(state.wheel);
  }

  /**
   * Apply PBR Automotive Paint Material properties
   */
  public applyPaint(paint: PaintSettings): void {
    this.applySpec('body', derivePaintSpec(paint));
  }

  /**
   * Apply 3D Stance, Suspension Ride Height & Rake Kinematics
   */
  public applyStance(stance: StanceSettings, config: ModelConfig): void {
    if (!this.chassisGroup) return;

    // 1 inch = 0.0254 meters
    const frontDropM = stance.frontHeightOffsetInches * 0.0254;
    const rearDropM = stance.rearHeightOffsetInches * 0.0254;

    // Average vertical chassis drop
    const avgDropM = (frontDropM + rearDropM) / 2;

    // Suspension Rake Angle (pitch around transverse axis)
    const rakeAngleRad = Math.atan2(frontDropM - rearDropM, config.wheelbaseMeters);

    // Apply affine transform to car chassis
    this.chassisGroup.position.y = avgDropM;

    // In Three.js coordinate system where car points along X or Z:
    if (Math.abs(config.rotationY) > 0.1) {
      this.chassisGroup.rotation.x = rakeAngleRad;
    } else {
      this.chassisGroup.rotation.z = -rakeAngleRad;
    }

    // Scale wheels dynamically based on diameter (15" is baseline 1.0)
    const frontScale = stance.frontWheelDiameter / 15.0;
    const rearScale = stance.rearWheelDiameter / 15.0;

    if (this.wheelNodes.frontLeft) this.wheelNodes.frontLeft.scale.setScalar(frontScale);
    if (this.wheelNodes.frontRight) this.wheelNodes.frontRight.scale.setScalar(frontScale);
    if (this.wheelNodes.rearLeft) this.wheelNodes.rearLeft.scale.setScalar(rearScale);
    if (this.wheelNodes.rearRight) this.wheelNodes.rearRight.scale.setScalar(rearScale);

    if (this.wheelNodes.all.length > 0 && !this.wheelNodes.frontLeft) {
      // Fallback: apply average scale to all wheels
      const avgScale = (frontScale + rearScale) / 2;
      for (const w of this.wheelNodes.all) {
        w.scale.setScalar(avgScale);
      }
    }
  }

  /**
   * Apply Wheel Finish (chrome, polished, machined, matte black, gold)
   */
  public applyWheels(wheel: WheelSettings): void {
    this.applySpec('wheel_rim', deriveWheelSpec(wheel.finish));
    this.applySpec('tire', deriveTireSpec(wheel.tireTreatment));
  }

  /**
   * Transition smoothly to a camera angle preset
   */
  public setCameraAngle(presetId: CameraPresetId): void {
    const preset = CAMERA_PRESETS[presetId];
    if (!preset || !this.camera || !this.controls) return;

    this.currentCameraPreset = presetId;
    const pose = this.solveCurrentPose(preset);
    if (!pose) return;

    this.cameraStartPos.copy(this.camera.position);
    this.cameraEndPos.set(pose.position[0], pose.position[1], pose.position[2]);
    this.cameraStartTarget.copy(this.controls.target);
    this.cameraEndTarget.set(pose.target[0], pose.target[1], pose.target[2]);

    this.cameraTransitionStart = performance.now();
    this.isTransitioningCamera = true;
  }

  /** Solve a preset against the loaded model, or null before one has loaded. */
  private solveCurrentPose(preset: CameraPreset): { position: [number, number, number]; target: [number, number, number] } | null {
    if (!this.modelBounds || !this.camera) return null;
    return solveCameraPose(preset, this.modelBounds, this.camera.fov, this.camera.aspect);
  }

  /** Snap to a preset with no transition (used on model load and on resize). */
  private applyCameraPresetImmediately(presetId: CameraPresetId): void {
    const preset = CAMERA_PRESETS[presetId];
    if (!preset || !this.camera || !this.controls) return;
    const pose = this.solveCurrentPose(preset);
    if (!pose) return;

    this.isTransitioningCamera = false;
    this.camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
    this.controls.target.set(pose.target[0], pose.target[1], pose.target[2]);
    this.controls.update();
  }

  /**
   * Re-frame without discarding the user's own orbit. OrbitControls owns the
   * camera position once the user has dragged, so re-solving the preset on every
   * resize would fight them; this only re-fits when nothing has been orbited.
   */
  private frameCamera(presetId: CameraPresetId): void {
    if (!this.camera || !this.controls) return;
    const pose = this.solveCurrentPose(CAMERA_PRESETS[presetId]);
    if (!pose) return;
    if (this.userHasOrbited) return;
    this.applyCameraPresetImmediately(presetId);
  }

  /**
   * Toggle turntable auto-rotation
   */
  public setTurntableAutoRotate(enabled: boolean): void {
    this.autoRotateTurntable = enabled;
  }

  public isTurntableAutoRotating(): boolean {
    return this.autoRotateTurntable;
  }

  public getLightingMode(): LightingPresetId {
    return this.currentLightingMode;
  }

  public getContainer(): HTMLElement | null {
    return this.container;
  }

  /** Model bounds in world units and the live camera distance, for QA diagnosis. */
  public getFramingInfo(): { size: [number, number, number]; center: [number, number, number]; cameraDistance: number } {
    let size: [number, number, number] = [0, 0, 0];
    let center: [number, number, number] = [0, 0, 0];
    if (this.modelBounds) {
      const s = this.modelBounds.getSize(new THREE.Vector3());
      const c = this.modelBounds.getCenter(new THREE.Vector3());
      size = [s.x, s.y, s.z];
      center = [c.x, c.y, c.z];
    }
    const cameraDistance = this.camera && this.modelBounds
      ? this.camera.position.distanceTo(this.controls?.target ?? new THREE.Vector3())
      : 0;
    return { size, center, cameraDistance };
  }

  /** Draw-call and triangle counts, for the headless render-metrics gate. */
  public getRenderInfo(): { drawCalls: number; triangles: number } {
    return {
      drawCalls: this.renderer?.info.render.calls ?? 0,
      triangles: this.renderer?.info.render.triangles ?? 0,
    };
  }

  /**
   * How many meshes landed in each render role, plus the total mesh count.
   * The QA gate asserts on this: a silhouette whose body role is empty will
   * render, but the paint rack will silently do nothing to it.
   */
  public getRoleCounts(): { body: number; wheel_rim: number; tire: number; brake: number; glass: number; totalMeshes: number } {
    const counts = { body: 0, wheel_rim: 0, tire: 0, brake: 0, glass: 0, totalMeshes: 0 };
    if (!this.carRootGroup) return counts;

    const byMaterial = new Map<THREE.Material, MeshRole>();
    for (const [role, material] of Object.entries(this.roleMaterials ?? {})) {
      byMaterial.set(material, role as MeshRole);
    }

    this.carRootGroup.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      counts.totalMeshes += 1;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        const role = byMaterial.get(material);
        if (role && role !== 'other') counts[role] += 1;
      }
    });

    return counts;
  }

  /** The authored names of every mesh that kept its original material. */
  public getUnclassifiedMeshes(): string[] {
    const unclassified: string[] = [];
    if (!this.carRootGroup) return unclassified;

    const classified = new Set<THREE.Material>(Object.values(this.roleMaterials ?? {}));
    this.carRootGroup.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      if (!materials.some((material) => classified.has(material))) {
        const materialNames = materials.map((m) => m.name || '(unnamed)').join('+');
        unclassified.push(`${mesh.name || '(unnamed)'}[${materialNames}]`);
      }
    });

    return unclassified;
  }

  /**
   * Export high-resolution PNG snapshot from WebGL context
   */
  public exportSnapshot(filename?: string): string {
    if (!this.renderer || !this.scene || !this.camera) return '';

    // Render fresh frame
    this.renderer.render(this.scene, this.camera);
    const dataUrl = this.renderer.domElement.toDataURL('image/png');

    if (filename && typeof document !== 'undefined') {
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    }

    return dataUrl;
  }

  /**
   * Main requestAnimationFrame animation loop
   */
  private startAnimationLoop(): void {
    const animate = () => {
      this.animationFrameId = requestAnimationFrame(animate);

      // Handle smooth camera transition lerp
      if (this.isTransitioningCamera && this.camera && this.controls) {
        const now = performance.now();
        const elapsed = now - this.cameraTransitionStart;
        const t = Math.min(1.0, elapsed / this.cameraTransitionDuration);

        // Ease in-out cubic
        const ease = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        this.camera.position.lerpVectors(this.cameraStartPos, this.cameraEndPos, ease);
        this.controls.target.lerpVectors(this.cameraStartTarget, this.cameraEndTarget, ease);

        if (t >= 1.0) {
          this.isTransitioningCamera = false;
        }
      }

      // Turntable slow rotation
      if (this.autoRotateTurntable && this.carRootGroup) {
        this.carRootGroup.rotation.y += 0.005;
      }

      // Update OrbitControls
      this.controls?.update();

      // Render Scene
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    };

    animate();
  }

  /**
   * Clean up WebGL resources and event listeners
   */
  public destroy(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.controls) {
      this.controls.dispose();
      this.controls = null;
    }

    if (this.dracoLoader) {
      this.dracoLoader.dispose();
      this.dracoLoader = null;
    }
    this.gltfLoader = null;
    this.environmentTexture = null;
    this.roleMaterials = null;

    if (this.scene) {
      this.disposeObject(this.scene);
      this.scene = null;
    }

    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement.parentElement) {
        this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
      }
      this.renderer = null;
    }

    this.container = null;
  }

  private disposeObject(obj: THREE.Object3D): void {
    obj.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) {
          mesh.geometry.dispose();
        }
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      }
    });
  }
}
